import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, Button, Accordion, AccordionSummary, AccordionDetails, Typography, Chip, Divider, Box } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Key as KeyIcon, Edit as EditIcon } from '@mui/icons-material';

interface WorkflowInputsDrawerProps {
  open: boolean;
  onClose: () => void;
  inputs: Record<string, any>;
  newInputKey: string;
  newInputValue: string;
  onNewInputKeyChange: (value: string) => void;
  onNewInputValueChange: (value: string) => void;
  onAddInput: () => void;
  onInputChange: (key: string, value: string) => void;
  onDeleteInput: (key: string) => void;
}

const WorkflowInputsDrawer: React.FC<WorkflowInputsDrawerProps> = ({
  open,
  onClose,
  inputs,
  newInputKey,
  newInputValue,
  onNewInputKeyChange,
  onNewInputValueChange,
  onAddInput,
  onInputChange,
  onDeleteInput,
}) => {
  const inputCount = Object.keys(inputs).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ p: 1, bgcolor: 'blue.100', borderRadius: '50%' }}>
            <KeyIcon className="text-blue-600" />
          </Box>
          <Box>
            <Typography variant="h6" className="m-0">Workflow Parameters</Typography>
            <Typography variant="body2" color="text.secondary">Configure dynamic inputs</Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Chip label={`${inputCount} parameter${inputCount !== 1 ? 's' : ''}`} color="primary" />
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ background: 'linear-gradient(135deg, #f0f7ff, #e6f7ff)' }}>
        <Box className="p-0 bg-blue-50 border-b border-blue-100">
          <Typography variant="h6" className="flex items-center gap-2 text-blue-700 mb-3">
            <AddIcon className="bg-blue-500 text-white p-1 rounded-full" />
            Add New Parameter
          </Typography>
          <Stack direction="column" spacing={2} className="w-full">
            <TextField
              placeholder="Parameter Name (e.g., user_name, api_key)"
              value={newInputKey}
              onChange={(e) => onNewInputKeyChange(e.target.value)}
              InputProps={{
                startAdornment: <KeyIcon className="text-blue-500" />
              }}
              className="border-blue-200 hover:border-blue-400 focus:border-blue-500"
              fullWidth
            />
            <TextField
              placeholder="Default Value (optional)"
              value={newInputValue}
              onChange={(e) => onNewInputValueChange(e.target.value)}
              InputProps={{
                startAdornment: <EditIcon className="text-green-500" />
              }}
              className="border-green-200 hover:border-green-400 focus:border-green-500"
              fullWidth
            />
            <Button 
              variant="contained"
              onClick={onAddInput} 
              disabled={!newInputKey.trim()}
              startIcon={<AddIcon />}
              className="bg-gradient-to-r from-blue-500 to-blue-600 border-0 h-10"
              fullWidth
            >
              Add Parameter
            </Button>
          </Stack>
        </Box>
        <Divider className="m-0" sx={{ my: 2 }} />
        <Box className="p-0 overflow-auto" style={{ maxHeight: '40vh' }}>
          <Typography variant="h6" className="flex items-center gap-2 text-gray-700 mb-3">
            <EditIcon className="bg-gray-200 text-gray-700 p-1 rounded-full" />
            Current Parameters
          </Typography>
          {inputCount === 0 ? (
            <Box className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
              <div className="text-4xl mb-2">📝</div>
              <Typography variant="subtitle1" fontWeight="bold">No parameters configured</Typography>
              <Typography variant="body2" color="text.secondary" className="mt-1">
                Add parameters above to make your workflow dynamic
              </Typography>
            </Box>
          ) : (
            <Stack direction="column" spacing={2} className="w-full">
              {Object.entries(inputs).map(([key, value]) => (
                <Box 
                  key={key} 
                  className="p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200"
                >
                  <Box className="flex items-center justify-between mb-2">
                    <Box className="flex items-center gap-2">
                      <Box className="w-2 h-2 bg-blue-500 rounded-full"></Box>
                      <Typography variant="subtitle1" fontWeight="bold">{key}</Typography>
                      <Chip label="parameter" color="primary" size="small" />
                    </Box>
                    <Button 
                      variant="text"
                      color="error"
                      startIcon={<DeleteIcon />} 
                      onClick={() => onDeleteInput(key)}
                      size="small"
                      className="opacity-70 hover:opacity-100"
                    />
                  </Box>
                  <Box className="mt-1">
                    <Typography variant="caption" color="text.secondary" className="uppercase font-semibold block mb-1">
                      Current Value
                    </Typography>
                    <TextField
                      value={value}
                      onChange={(e) => onInputChange(key, e.target.value)}
                      className="border-gray-200"
                      size="small"
                      fullWidth
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
        {inputCount > 0 && (
          <Box className="p-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center gap-1">
            <Box className="w-2 h-2 bg-blue-400 rounded-full"></Box>
            Parameters will be available throughout your workflow execution
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowInputsDrawer;